/**
 * The snapshot store every provider publishes through.
 *
 * `useSyncExternalStore` demands a stable reference between notifications, so
 * every write produces a NEW frozen-by-convention snapshot and nothing mutates
 * in place. `setRoster` additionally drops writes that change nothing
 * observable — LiveKit fires speaker and quality events several times a second
 * and the meeting tree must not re-render on each of them.
 */
import type { MediaParticipant, MediaSnapshot } from './contract';
import { rosterSignature } from './livekitRoster';
import { initialSnapshot, type MediaRoomSeed } from './roomSeed';

export interface MediaStore {
  subscribe(listener: () => void): () => void;
  get(): MediaSnapshot;
  patch(patch: Partial<MediaSnapshot>): void;
  /** Optimistic single-participant edit; the next roster sync may correct it. */
  patchParticipant(id: string, patch: Partial<MediaParticipant>): void;
  /** Optimistic whole-list edit (host removed someone, spotlight moved). */
  setParticipants(participants: readonly MediaParticipant[]): void;
  setRoster(
    participants: readonly MediaParticipant[],
    localId: string,
    screenShareOwnerId: string | null,
  ): void;
}

export function createMediaStore(seed: MediaRoomSeed): MediaStore {
  const listeners = new Set<() => void>();
  let snapshot: MediaSnapshot = initialSnapshot(seed);
  let rosterKey = '';

  const patch = (next: Partial<MediaSnapshot>): void => {
    snapshot = { ...snapshot, ...next };
    for (const listener of listeners) listener();
  };

  /** Any optimistic edit must be re-checked against the room on the next sync. */
  const invalidate = (participants: readonly MediaParticipant[]): void => {
    rosterKey = '';
    patch({ participants });
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    get: () => snapshot,
    patch,

    patchParticipant(id, next) {
      invalidate(snapshot.participants.map((p) => (p.id === id ? { ...p, ...next } : p)));
    },

    setParticipants: invalidate,

    setRoster(participants, localId, screenShareOwnerId) {
      const key = rosterSignature(participants);
      if (key === rosterKey && screenShareOwnerId === snapshot.screenShareOwnerId) return;
      rosterKey = key;
      patch({ participants, localId, screenShareOwnerId });
    },
  };
}
